/**
 * Mock user service — create/update/invite logic moved out of the old
 * userStore, with the audit-log side-effects kept here (mock mode only).
 */

import {
  mockUsers,
  mockInvitations,
  getUsersByTenant,
  getPendingInvitations,
} from '@/data/mockUsers'
import { addAuditEntry } from '@/data/mockAuditLog'
import { mockDelay } from '@/services/factory'
import { normalizeRole } from '@/security/accessContract'
import type { UserService } from './users.service'
import type {
  User,
  UserRole,
  UserPreferences,
  CreateUserInput,
  UpdateUserInput,
  UserInvitation,
  InviteUserInput,
} from '@/types/user.types'

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

export function createMockUserService(): UserService {
  return {
    async list(tenantId: string): Promise<User[]> {
      await mockDelay(300)
      return getUsersByTenant(tenantId)
    },

    async create(input: CreateUserInput, tenantId: string): Promise<User> {
      await mockDelay(500)
      const effectiveRole = normalizeRole(input.role)
      const isClient =
        effectiveRole === 'individual_client' ||
        effectiveRole === 'business_owner_client' ||
        effectiveRole === 'authorized_client' ||
        effectiveRole === 'client'
      const newUser: User = {
        userId: `user-${Date.now()}`,
        email: input.email,
        fullName: input.fullName,
        firstName: input.fullName.split(' ')[0],
        lastName: input.fullName.split(' ').slice(1).join(' '),
        phone: input.phone,
        tenantId,
        role: input.role,
        userCategory: isClient ? 'client' : 'ca_staff',
        isActive: true,
        isVerified: false,
        failedLogins: 0,
        preferences: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      mockUsers.push(newUser)

      addAuditEntry({
        action: 'user_created',
        resourceType: 'user',
        resourceId: newUser.userId,
        resourceName: newUser.fullName,
        details: {
          targetUserId: newUser.userId,
          targetUserEmail: newUser.email,
          newRole: newUser.role,
        },
        success: true,
      })
      return newUser
    },

    async update(userId: string, updates: UpdateUserInput): Promise<Partial<User>> {
      await mockDelay(300)
      addAuditEntry({
        action: 'user_updated',
        resourceType: 'user',
        resourceId: userId,
        details: { changedFields: Object.keys(updates) },
        success: true,
      })
      return { ...updates, updatedAt: new Date().toISOString() }
    },

    async updateMe(updates: UpdateUserInput): Promise<Partial<User>> {
      await mockDelay(300)
      addAuditEntry({
        action: 'user_updated',
        resourceType: 'user',
        resourceId: 'me',
        details: { changedFields: Object.keys(updates) },
        success: true,
      })
      const patch: Partial<User> = { updatedAt: new Date().toISOString() }
      if (updates.fullName !== undefined) patch.fullName = updates.fullName
      if (updates.preferences !== undefined) {
        patch.preferences = updates.preferences as UserPreferences
      }
      return patch
    },

    async updatePreferences(preferences: UserPreferences): Promise<UserPreferences> {
      await mockDelay(300)
      addAuditEntry({
        action: 'user_updated',
        resourceType: 'user',
        resourceId: 'me',
        details: { changedFields: ['preferences'] },
        success: true,
      })
      return preferences
    },

    async remove(userId: string, _reason?: string): Promise<void> {
      await mockDelay(300)
      const user = mockUsers.find(u => u.userId === userId)
      addAuditEntry({
        action: 'user_deleted',
        resourceType: 'user',
        resourceId: userId,
        resourceName: user?.fullName,
        details: { targetUserEmail: user?.email ?? null },
        success: true,
      })
    },

    async setActive(userId: string, isActive: boolean): Promise<Partial<User>> {
      await mockDelay(300)
      addAuditEntry({
        action: isActive ? 'user_activated' : 'user_deactivated',
        resourceType: 'user',
        resourceId: userId,
        success: true,
      })
      return { isActive, updatedAt: new Date().toISOString() }
    },

    async assignRole(userId: string, role: UserRole, oldRole?: UserRole): Promise<Partial<User>> {
      await mockDelay(300)
      addAuditEntry({
        action: 'role_assigned',
        resourceType: 'user',
        resourceId: userId,
        details: { oldRole: oldRole ?? null, newRole: role },
        success: true,
      })
      return { role, updatedAt: new Date().toISOString() }
    },

    async listInvitations(tenantId: string): Promise<UserInvitation[]> {
      await mockDelay(200)
      return getPendingInvitations(tenantId)
    },

    async invite(
      input: InviteUserInput,
      tenantId: string,
      invitedBy: string
    ): Promise<UserInvitation> {
      await mockDelay(500)
      const newInvitation: UserInvitation = {
        invitationId: `inv-${Date.now()}`,
        tenantId,
        email: input.email,
        fullName: input.fullName,
        role: input.role,
        status: 'pending',
        invitedBy,
        message: input.message,
        token: `inv-token-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        expiresAt: new Date(Date.now() + WEEK_MS).toISOString(),
        createdAt: new Date().toISOString(),
      }
      mockInvitations.push(newInvitation)

      addAuditEntry({
        action: 'invitation_sent',
        resourceType: 'invitation',
        resourceId: newInvitation.invitationId,
        details: { targetUserEmail: input.email, newRole: input.role },
        success: true,
      })
      return newInvitation
    },

    async cancelInvitation(invitationId: string): Promise<void> {
      await mockDelay(200)
      addAuditEntry({
        action: 'invitation_cancelled',
        resourceType: 'invitation',
        resourceId: invitationId,
        success: true,
      })
    },

    async resendInvitation(_invitationId: string): Promise<{ expiresAt: string }> {
      await mockDelay(300)
      return { expiresAt: new Date(Date.now() + WEEK_MS).toISOString() }
    },
  }
}
